import React from 'react'
import classNames from 'classnames'

import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles'

import { FormattedNumber } from 'react-intl'
import Grid, { GridProps } from '@material-ui/core/Grid'

import GameCardContent from '../GameCardContent'

import Avatar from '@material-ui/core/Avatar'
import Typography from '@material-ui/core/Typography'
import ordinalize, { ordinalizeSuffix } from 'lib/ordinalize'

import defaultAvatar from 'assets/images/default_user_avatar.png'

interface Props extends GridProps {
  rank: number
  winnings: number
  bestOpponent: {
    rank: number
    points: number
    photoUrl: string
  }
}

const styles = createStyles({
  separator: {
    width: 1,
    height: '50%',
    opacity: .2,
    background: `
      linear-gradient(
        to bottom,
        transparent,
        black,
        transparent
      )
    `
  },
  padX: {
    paddingLeft: 8,
    paddingRight: 8
  },
  padLeft: {
    paddingLeft: 8
  },
  padRight: {
    paddingRight: 8
  },
  avatar: {
    width: 26,
    height: 26,
    borderWidth: 3,
    borderStyle: 'solid',
    borderColor: 'white',
    marginRight: 4
  },
  small: {
    fontSize: 10,
    fontWeight: 500
  },
  smaller: {
    fontSize: 9,
    fontWeight: 500
  },
  transparent: {
    opacity: .5
  },
  rank: {
    fontSize: 42,
    fontWeight: 500
  },
  rankSuffix: {
    fontSize: 16,
    lineHeight: 1.5,
    marginLeft: 3,
    verticalAlign: 'top'
  },
  success: {
    color: '#01E99E'
  },
  error: {
    color: '#FF7888'
  }
})

type InjectedProps = Props & WithStyles<typeof styles>

const GameCardContentCompleted: React.SFC<InjectedProps> = ({
  classes,
  className,
  rank,
  winnings,
  bestOpponent,
  ...props
}) => {
  // TODO: translation key(s) needed

  const hasWinnings = (winnings || 0) > 0
  const variant = hasWinnings ? 'success' : 'error'

  return (
    <GameCardContent
      variant={variant}
      alignItems="center"
      wrap="nowrap"
      {...props}
    >
      <Grid className={classes.padX} xs item>
        <Typography
          className={classNames(classes.rank, classes[variant])}
          variant="h3"
          align="center"
          noWrap
        >
          <span>{rank}</span>
          <strong className={classes.rankSuffix}>{ordinalizeSuffix(rank)}</strong>
        </Typography>
      </Grid>

      <Grid className={classes.padX} zeroMinWidth xs item>
        <Typography className={classNames(classes.small, classes.transparent)} noWrap>
          <strong>{hasWinnings ? 'Congratulations!' : 'It was a long shot...'}</strong><br />
          <span>{hasWinnings ? 'You won ' : 'You lost '}</span>
          <strong>
            <FormattedNumber value={winnings}>
              {(value: string) => {
                if (winnings > 0) {
                  return `+${value}k!`
                }
                return `${value}k.`
              }}
            </FormattedNumber>
          </strong>
        </Typography>
      </Grid>

      <Grid className={classes.separator} item />

      <Grid className={classes.padX} xs item>
        <Grid direction="row" alignItems="center" wrap="nowrap" container>
          <Grid item>
            <Avatar className={classes.avatar} src={bestOpponent.photoUrl || defaultAvatar} />
          </Grid>
          <Grid item>
            <Typography className={classNames(classes.smaller, classes.transparent)} noWrap>
              <strong>{ordinalize(bestOpponent.rank)}</strong><br />
              <span>{`${bestOpponent.points} Points`}</span>
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </GameCardContent>
  )
}

GameCardContentCompleted.defaultProps = {
  winnings: 0,
}

export default withStyles(styles)(GameCardContentCompleted)
